<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { Feature, PageName } from '@/api/tracking';
import { INITIAL_VISIBLE_DOCUMENT_COUNT } from '@/constants';
import FeedDateSelectbox from '@/components/common/feed-date-selectbox.vue';
import {
  createTechnicalLiteratureShouldEnable,
  useAutoGenerateSummary,
} from '@/components/feed-summary/composables/use-auto-generate-summary';
import { usePersonalFeedSummary } from '@/components/feed-summary/composables/use-personal-feed-summary';
import FeedSummaryGenerationButton from '@/components/feed-summary/feed-summary-generation-button.vue';
import FeedSummary from '@/components/feed-summary/feed-summary.vue';
import PersonalAdpDocumentList from '@/components/personal-news/personal-adp-document-list.vue';
import {
  ArticleMarkSource,
  FeedType,
  ThemeFeedPatent,
  ThemeFeedResearchPaper,
} from '@/types';

type Props = {
  docType: 'research_paper' | 'patent';
  feedType: FeedType;
  domesticDocuments: (ThemeFeedResearchPaper | ThemeFeedPatent)[];
  foreignDocuments: (ThemeFeedResearchPaper | ThemeFeedPatent)[];
  dateOptions: { label: string; value: string }[];
  isPast: boolean;
  feedDate: string;
};
const props = defineProps<Props>();

const emit = defineEmits<{
  'select-date': [value: string | undefined];
}>();

const title = props.docType === 'research_paper' ? '論文ジャーナル' : '特許';

const maxFeedCount = 50;
const pageName: PageName = `personal_${props.docType}s`;
const feature: Feature = `personal_${props.docType}_feed`;
const markSource: ArticleMarkSource = 'personal_feed';

const initialDate = props.dateOptions[props.isPast ? 1 : 0].value;
const currentDate = ref(initialDate);
const selectDate = (value: string | undefined) => {
  if (value === undefined) return;

  emit('select-date', value);

  // セレクトボックスの表示を強制的に戻すために、
  // 選択された値を代入してから元の値を入れ直して変更検知を走らせる
  currentDate.value = value;
  nextTick(() => {
    currentDate.value = initialDate;
  });
};

const articleCount = computed(() => {
  if (props.feedType === 'domestic') {
    return props.domesticDocuments.length;
  } else if (props.feedType === 'foreign') {
    return props.foreignDocuments.length;
  }
  return props.domesticDocuments.length + props.foreignDocuments.length;
});

const { summary, generateButtonState, generateSummary, shouldShowSummary } =
  usePersonalFeedSummary(
    {
      page_type: props.isPast ? 'past_feed' : 'feed',
      feed_type: props.feedType,
      doc_type: props.docType,
      date: props.feedDate,
      execution: 'morning', // 技術文献は1回配信なので
    },
    articleCount,
    pageName,
  );

// URLにsummarizeパラメータがあったら自動で要約生成する
// isPastでないとき（最新日付 = 先頭）は画面遷移時にも自動生成する
const { autoGenerationReason } = useAutoGenerateSummary(
  generateButtonState,
  generateSummary,
  feature,
  createTechnicalLiteratureShouldEnable({
    isPast: props.isPast,
    dateOptions: props.dateOptions,
  }),
);

// 過去文献で自動生成が有効ならスクロールする
watch(autoGenerationReason, autoGenReason => {
  if (autoGenReason !== undefined && props.isPast) {
    emit('select-date', props.dateOptions[1].value);
  }
});
</script>

<template>
  <div class="technical-literature-list">
    <div class="date-header">
      <FeedDateSelectbox
        :options="dateOptions"
        :model-value="currentDate"
        @update:model-value="selectDate"
      />
      <FeedSummaryGenerationButton
        :state="generateButtonState"
        class="generation-button"
        @click="generateSummary()"
      />
    </div>
    <hr class="header-divider" />
    <FeedSummary
      v-if="shouldShowSummary(summary)"
      :feed-summary="summary"
      :feature="feature"
      :page-name="pageName"
      class="feed-summary"
    />
    <template v-if="feedType === 'all' || feedType === 'domestic'">
      <PersonalAdpDocumentList
        :title="`国内${title}`"
        :display-feed-type="feedType"
        :theme-feed-type="'domestic'"
        :doc-type="docType"
        :adp-documents="domesticDocuments"
        :initial-feed-count="INITIAL_VISIBLE_DOCUMENT_COUNT"
        :max-feed-count="maxFeedCount"
        :page-name="pageName"
        :feature="feature"
        :mark-source="markSource"
      />
    </template>
    <template
      v-if="
        docType === 'research_paper' &&
        (feedType === 'all' || feedType === 'foreign')
      "
    >
      <PersonalAdpDocumentList
        :title="`海外${title}`"
        :display-feed-type="feedType"
        :theme-feed-type="'foreign'"
        :doc-type="docType"
        :adp-documents="foreignDocuments"
        :initial-feed-count="INITIAL_VISIBLE_DOCUMENT_COUNT"
        :max-feed-count="maxFeedCount"
        :page-name="pageName"
        :feature="feature"
        :mark-source="markSource"
      />
    </template>
  </div>
</template>

<style lang="scss" scoped>
.technical-literature-list {
  display: flex;
  flex-direction: column;

  background: $color-white;
  border-radius: 4px;
}

.date-header {
  position: sticky;
  top: var(--layout-sticky-top-under-tabs, 112px);
  z-index: 1;

  display: flex;
  align-items: center;
  height: 64px;
  padding: 0 16px;
  background: $color-white;
  border-radius: 4px;

  .generation-button {
    margin-left: auto;
  }
}

.header-divider {
  border: none;
  border-top: 1px solid $color-gray200;
  padding-bottom: 16px;
  background: $color-white;
}

.feed-summary {
  margin: 0 12px 16px;
}
</style>
